import {
  CalendarOutlined,
  CloseOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  HomeOutlined,
  IdcardOutlined,
  PhoneOutlined,
  PlusCircleOutlined,
  SaveOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { DatePicker, Input, Modal, Popconfirm, Select, message } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { motion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import { CREATE_jwel } from "../../../Config/Config";

// Display format for date pickers (stored value stays YYYY-MM-DD)
const DATE_DISPLAY_FORMAT = "DD-MM-YYYY";

const gradients = {
  header: "linear-gradient(120deg, #0B1F3D 0%, #1E3A8A 55%, #5B21B6 100%)",
  goldBar: "linear-gradient(90deg, #F5D06F, #B8860B 50%, #F5D06F)",
  delete: "linear-gradient(135deg, #FB7185, #BE123C)",
  saveAdd: "linear-gradient(135deg, #34D399, #059669)",
  saveEdit: "linear-gradient(135deg, #FBBF24, #B45309)",
};

const sections = {
  contact: { chip: "linear-gradient(135deg, #818CF8, #4338CA)", light: "linear-gradient(135deg, #EEF2FF, #F5F3FF)", accent: "#4338CA" },
  location: { chip: "linear-gradient(135deg, #34D399, #047857)", light: "linear-gradient(135deg, #ECFDF5, #F0FDFA)", accent: "#047857" },
  address: { chip: "linear-gradient(135deg, #FBBF24, #B45309)", light: "linear-gradient(135deg, #FFFBEB, #FEF3C7)", accent: "#B45309" },
  tax: { chip: "linear-gradient(135deg, #F472B6, #BE185D)", light: "linear-gradient(135deg, #FDF2F8, #FCE7F3)", accent: "#BE185D" },
  dates: { chip: "linear-gradient(135deg, #60A5FA, #1D4ED8)", light: "linear-gradient(135deg, #EFF6FF, #DBEAFE)", accent: "#1D4ED8" },
};

const cardVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.32, ease: [0.16, 1, 0.3, 1] } }),
};

const labelStyle = { display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#4B5563", marginBottom: 5, fontWeight: 600 };

const SectionCard = ({ id, icon, title, index, children }) => {
  const s = sections[id];
  return (
    <motion.div
      variants={cardVariants}
      custom={index}
      initial="hidden"
      animate="visible"
      style={{
        background: s.light,
        border: `1px solid ${s.accent}26`,
        borderRadius: 12,
        padding: 14,
        marginBottom: 12,
        "--accent-color": s.accent,
        "--accent-glow": `${s.accent}29`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <div
          style={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            background: s.chip,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontSize: 12,
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: s.accent }}>{title}</span>
      </div>
      {children}
    </motion.div>
  );
};

const CustomerDialog = ({
  open,
  mode,
  onCancel,
  onSave,
  onDelete,
  deleteCustomerDetailsAPI, // back-compat alias used by SalesInvoiceReturn
  formValues,
  setFormValues,
  tagNoRef,
  smCodeRef, // back-compat: focus target after cancel/close on SalesInvoiceReturn
  customerLookupAPI,
}) => {
  const [saving, setSaving] = useState(false);
  const [mobileWarning, setMobileWarning] = useState("");
  const [areaList, setAreaList] = useState([]);
  const [loadingArea, setLoadingArea] = useState(false);
  const [searchAreaText, setSearchAreaText] = useState("");
  const [stateList, setStateList] = useState([]);
  const [loadingState, setLoadingState] = useState(false);
  const [searchStateText, setSearchStateText] = useState("");
  const isEdit = mode === "edit";
  const handleDelete = onDelete || deleteCustomerDetailsAPI;
  const focusBackRef = tagNoRef || smCodeRef;

  const nameRef = useRef(null);
  const mobileRef = useRef(null);
  const cityRef = useRef(null);
  const address1Ref = useRef(null);
  const address2Ref = useRef(null);
  const address3Ref = useRef(null);
  const address4Ref = useRef(null);
  const stateRef = useRef(null);
  const stateCodeRef = useRef(null);
  const gstinRef = useRef(null);
  const dobRef = useRef(null);
  const anniversaryRef = useRef(null);
  const saveBtnRef = useRef(null);

  const pendingAreaPromisesRef = useRef(new Map());

  const set = (key) => (val) => setFormValues((prev) => ({ ...prev, [key]: val }));
  const onInput = (key) => (e) => set(key)(e.target.value);

  const fetchAreaMaster = async () => {
    try {
      setLoadingArea(true);
      const tenantName = localStorage.getItem("tenantName");
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableName?tableName=AREA_MAST`,
        { headers: { tenantName } }
      );
      const data = response?.data;
      if (Array.isArray(data)) {
        const uniqueAreas = Array.from(
          new Set(
            data
              .map(
                (item) =>
                  (
                    item?.Areaname ||
                    item?.areaname ||
                    item?.AREANAME ||
                    ""
                  )?.trim()
              )
              .filter((name) => name && name !== "-")
          )
        ).sort((a, b) => a.localeCompare(b));
        setAreaList(uniqueAreas);
      }
    } catch (error) {
      console.error("Error fetching AREA_MAST:", error);
    } finally {
      setLoadingArea(false);
    }
  };

  const saveAreaMaster = async (name) => {
    const trimmed = (name || "").trim();
    if (!trimmed || trimmed === "-") return false;
    const upper = trimmed.toUpperCase();

    // If already exists in areaList, no need to call API
    if (areaList.some((a) => a.toUpperCase() === upper)) {
      return true;
    }

    // Reuse existing in-flight promise for this area to avoid concurrent duplicates
    if (pendingAreaPromisesRef.current.has(upper)) {
      return pendingAreaPromisesRef.current.get(upper);
    }

    const savePromise = (async () => {
      try {
        const tenantName = localStorage.getItem("tenantName");
        const payload = {
          areacode: "-",
          areaname: upper,
          clouD_UPLOAD: "true",
        };

        await axios.post(`${CREATE_jwel}/api/Master/AreaMast`, payload, {
          headers: {
            "Content-Type": "application/json",
            tenantName,
          },
        });

        setAreaList((prev) => {
          if (prev.some((a) => a.toUpperCase() === upper)) return prev;
          return [...prev, upper].sort((a, b) => a.localeCompare(b));
        });
        message.success(`Area "${upper}" added successfully`);
        return true;
      } catch (error) {
        console.error("Error saving new area to AREA_MAST:", error);
        message.error(`Failed to save area "${upper}"`);
        return false;
      } finally {
        pendingAreaPromisesRef.current.delete(upper);
      }
    })();

    pendingAreaPromisesRef.current.set(upper, savePromise);
    return savePromise;
  };

  const handleAreaChange = async (val) => {
    if (!val) {
      set("city")("");
      setSearchAreaText("");
      return;
    }
    const clean = val.trim().toUpperCase();
    set("city")(clean);
    setSearchAreaText("");

    const isExisting = areaList.some((a) => a.toUpperCase() === clean);
    if (!isExisting && clean !== "-") {
      await saveAreaMaster(clean);
    }
  };

  useEffect(() => {
    fetchAreaMaster();
  }, []);

  useEffect(() => {
    if (open && areaList.length === 0 && !loadingArea) {
      fetchAreaMaster();
    }
  }, [open]);

  const areaOptions = useMemo(() => {
    const list = [...areaList];
    const currentVal = formValues?.city?.trim()?.toUpperCase();
    if (
      currentVal &&
      currentVal !== "-" &&
      !list.some((a) => a.toUpperCase() === currentVal)
    ) {
      list.unshift(currentVal);
    }
    const options = list.map((name) => ({
      value: name,
      label: name,
    }));

    const searchTrimmed = searchAreaText.trim();
    if (
      searchTrimmed &&
      searchTrimmed !== "-" &&
      !list.some((a) => a.toLowerCase() === searchTrimmed.toLowerCase())
    ) {
      const upperSearch = searchTrimmed.toUpperCase();
      options.unshift({
        value: upperSearch,
        label: `${upperSearch} (Add new)`,
      });
    }

    return options;
  }, [areaList, formValues?.city, searchAreaText]);

  /* ===================== STATE_MASTER API Integration ===================== */

  const fetchStateMaster = async () => {
    try {
      setLoadingState(true);
      const tenantName = localStorage.getItem("tenantName");
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetDataFromGivenTableName?tableName=STATE_MASTER`,
        { headers: { tenantName } }
      );
      const data = response?.data;
      if (Array.isArray(data)) {
        const clean = data
          .filter(
            (item) =>
              item?.statename &&
              item.statename.trim() !== "-" &&
              item.statename.trim() !== ""
          )
          .map((item) => ({
            statename: item.statename.trim(),
            statecode: (item.STATECODE || item.statecode || "").trim(),
            defaultState: item.DEFAULTSTATE,
          }));
        setStateList(clean);
      }
    } catch (error) {
      console.error("Error fetching STATE_MASTER:", error);
    } finally {
      setLoadingState(false);
    }
  };

  useEffect(() => {
    fetchStateMaster();
  }, []);

  useEffect(() => {
    if (open && stateList.length === 0 && !loadingState) {
      fetchStateMaster();
    }
  }, [open]);

  const stateOptions = useMemo(() => {
    const list = [...stateList];
    const currentVal = formValues?.state?.trim();
    if (
      currentVal &&
      currentVal !== "-" &&
      !list.some((s) => s.statename.toLowerCase() === currentVal.toLowerCase())
    ) {
      list.unshift({
        statename: currentVal,
        statecode: formValues?.stateCode || "",
      });
    }

    const options = list.map((item) => ({
      value: item.statename,
      label: item.statename,
      statecode: item.statecode,
    }));

    const searchTrimmed = searchStateText.trim();
    if (
      searchTrimmed &&
      !list.some(
        (s) => s.statename.toLowerCase() === searchTrimmed.toLowerCase()
      )
    ) {
      options.unshift({
        value: searchTrimmed,
        label: `${searchTrimmed} (Add new)`,
        statecode: "",
      });
    }

    return options;
  }, [stateList, formValues?.state, formValues?.stateCode, searchStateText]);

  const resolveStation = (item) => {
    if (!item) return "LOCAL";
    const st = item.station || item.STATION;
    if (st && String(st).trim() !== "" && String(st).trim() !== "-") {
      return String(st).trim().toUpperCase();
    }
    const def = item.defaultState ?? item.DEFAULTSTATE ?? item.DEAFULTSTATE;
    if (def === true || def === "Y" || def === "true" || def === 1) {
      return "LOCAL";
    }
    return "OUTSTATION";
  };

  // Auto-populate stateCode and locationType (STATION) from stateList if state is set
  useEffect(() => {
    if (stateList.length > 0 && formValues?.state) {
      const selected = stateList.find(
        (s) => (s.statename || "").trim().toLowerCase() === (formValues.state || "").trim().toLowerCase()
      );
      if (selected) {
        setFormValues((prev) => {
          let updated = false;
          const next = { ...prev };
          if (!prev.stateCode && selected.statecode) {
            next.stateCode = selected.statecode;
            updated = true;
          }
          if (!prev.locationType) {
            next.locationType = resolveStation(selected);
            updated = true;
          }
          return updated ? next : prev;
        });
      }
    }
  }, [stateList, formValues?.state]);

  const handleStateChange = (val) => {
    const selected = stateList.find(
      (s) => (s.statename || "").trim().toLowerCase() === (val || "").trim().toLowerCase()
    );
    setFormValues((prev) => ({
      ...prev,
      state: val || "",
      stateCode: selected ? (selected.statecode || selected.STATECODE || "") : "",
      locationType: selected ? resolveStation(selected) : (prev.locationType || "LOCAL"),
    }));
    setSearchStateText("");
  };

  // Autofocus the first field whenever the dialog opens
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => nameRef.current?.focus(), 80);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Generic Enter handler for plain <Input> elements
  const focusNext = (nextRef) => (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      nextRef?.current?.focus?.();
    }
  };

  // Ant's Select / DatePicker sometimes need a tick before focus works
  const focusNextDeferred = (nextRef) => (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setTimeout(() => nextRef?.current?.focus?.(), 0);
    }
  };

  // duplicate-mobile check only when adding a NEW customer
  useEffect(() => {
    if (isEdit || !formValues?.mobile1 || formValues.mobile1.length !== 10) {
      setMobileWarning("");
      return;
    }
    const t = setTimeout(async () => {
      const existing = await customerLookupAPI?.(formValues.mobile1);
      setMobileWarning(existing ? `A customer already exists with this number: ${existing.Custname}` : "");
    }, 400);
    return () => clearTimeout(t);
  }, [formValues?.mobile1, isEdit]);

  const isValid = !!formValues?.name?.trim() && /^\d{10}$/.test(formValues?.mobile1 || "");

  const handleSave = async () => {
    if (!isValid) return;
    setSaving(true);
    try {
      const currentCity = formValues?.city?.trim();
      const upperCity = currentCity ? currentCity.toUpperCase() : "";
      if (
        upperCity &&
        upperCity !== "-" &&
        !areaList.some((a) => a.toUpperCase() === upperCity)
      ) {
        await saveAreaMaster(upperCity);
      }
      const updatedFormValues =
        upperCity && upperCity !== formValues?.city
          ? { ...formValues, city: upperCity }
          : formValues;
      await onSave(updatedFormValues);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    onCancel();
    setTimeout(() => focusBackRef?.current?.focus?.(), 0);
  };

  // Shortcut: Alt + S inside CustomerDialog saves the customer
  useEffect(() => {
    if (!open) return;
    const handleCustomerDialogShortcuts = (e) => {
      const isAltOnly = e.altKey && !e.ctrlKey && !e.metaKey;
      if (isAltOnly && ((e.key || "").toLowerCase() === "s" || e.code === "KeyS")) {
        e.preventDefault();
        e.stopPropagation();
        if (isValid && !saving) {
          handleSave();
        }
      }
    };
    window.addEventListener("keydown", handleCustomerDialogShortcuts, true);
    return () => {
      window.removeEventListener("keydown", handleCustomerDialogShortcuts, true);
    };
  }, [open, isValid, saving]);

  const saveGrad = isEdit ? gradients.saveEdit : gradients.saveAdd;
  const saveGlow = isEdit ? "rgba(180,83,9,0.4)" : "rgba(5,150,105,0.4)";

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      footer={null}
      centered
      width={680}
      closable={false}
      maskStyle={{ backdropFilter: "blur(8px)", backgroundColor: "rgba(11,15,30,0.35)" }}
      styles={{ content: { padding: 20, borderRadius: 14, boxShadow: "0 24px 60px rgba(30,27,75,0.35), 0 10px 24px rgba(0,0,0,0.15)" } }}
      afterOpenChange={(o) => {
        if (o) setTimeout(() => nameRef.current?.focus(), 80);
      }}
    >
      <style>{`
        .cd-dialog .ant-input:focus,
        .cd-dialog .ant-input-affix-wrapper-focused,
        .cd-dialog .ant-select-focused .ant-select-selector,
        .cd-dialog .ant-picker-focused {
          box-shadow: 0 0 0 3px var(--accent-glow, rgba(67,56,202,0.16)) !important;
          border-color: var(--accent-color, #4338CA) !important;
        }
        .cd-dialog .ant-input, .cd-dialog .ant-select-selector, .cd-dialog .ant-picker {
          border-radius: 8px !important;
        }
      `}</style>

      <div className="cd-dialog">
        {/* ===== Header ===== */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: gradients.header,
            padding: "16px 20px",
            borderRadius: "12px 12px 0 0",
            margin: "-20px -24px 0",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <motion.div
            style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 3, background: gradients.goldBar, backgroundSize: "200% 100%" }}
            animate={{ backgroundPosition: ["0% 0%", "200% 0%"] }}
            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <motion.div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: isEdit ? gradients.saveEdit : gradients.saveAdd,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontSize: 17,
                flexShrink: 0,
              }}
              animate={{ scale: [1, 1.08, 1] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            >
              <UserOutlined />
            </motion.div>
            <div style={{ color: "#fff", fontSize: 16, fontWeight: 700 }}>
              {isEdit ? "Edit Customer" : "Add New Customer"}
            </div>
          </div>
          <motion.button
            onClick={handleCancel}
            aria-label="Close"
            style={{
              width: 30, height: 30, borderRadius: 8, border: "none",
              background: "rgba(255,255,255,0.14)", color: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
            }}
            whileHover={{ rotate: 90, backgroundColor: "rgba(255,255,255,0.25)" }}
            whileTap={{ scale: 0.85 }}
            transition={{ duration: 0.25 }}
          >
            <CloseOutlined style={{ fontSize: 12 }} />
          </motion.button>
        </div>

        {/* ===== Body ===== */}
        <div style={{ padding: "20px 4px 4px" }}>
          <SectionCard id="contact" icon={<UserOutlined />} title="Contact details" index={0}>
            <div style={{ display: "flex", gap: 10 }}>
              <div style={{ flex: 1.4 }}>
                <label style={labelStyle}>Name *</label>
                <Input
                  ref={nameRef}
                  value={formValues?.name}
                  onChange={onInput("name")}
                  onFocus={(e) => e.target.select()}
                  onKeyDown={isEdit ? focusNextDeferred(cityRef) : focusNext(mobileRef)}
                  placeholder="Customer name"
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}><PhoneOutlined /> Mobile No *</label>
                <Input
                  ref={mobileRef}
                  value={formValues?.mobile1}
                  disabled={isEdit}
                  maxLength={10}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => set("mobile1")(e.target.value.replace(/\D/g, ""))}
                  onKeyDown={focusNextDeferred(cityRef)}
                />
                {mobileWarning && (
                  <div style={{ fontSize: 11, color: "#BE123C", marginTop: 4, fontWeight: 600 }}>{mobileWarning}</div>
                )}
              </div>
            </div>
          </SectionCard>

          <SectionCard id="location" icon={<EnvironmentOutlined />} title="Location" index={1}>
            <div style={{ display: "flex", gap: 10 }}>
              <div style={{ flex: 1.4 }}>
                <label style={labelStyle}>City / Area</label>
                <Select
                  ref={cityRef}
                  showSearch
                  allowClear
                  placeholder="Select or enter area"
                  style={{ width: "100%" }}
                  value={formValues?.city || undefined}
                  onChange={handleAreaChange}
                  onSearch={(val) => setSearchAreaText(val)}
                  onKeyDown={focusNextDeferred(stateRef)}
                  filterOption={(input, option) => {
                    const val = String(option?.value ?? "").toLowerCase();
                    const lbl = String(option?.label ?? "").toLowerCase();
                    const q = input.toLowerCase();
                    return val.includes(q) || lbl.includes(q);
                  }}
                  options={areaOptions}
                  loading={loadingArea}
                />
              </div>
              <div style={{ flex: 1.4 }}>
                <label style={labelStyle}>State</label>
                <Select
                  ref={stateRef}
                  showSearch
                  allowClear
                  placeholder="Select state"
                  style={{ width: "100%" }}
                  value={formValues?.state || undefined}
                  onChange={handleStateChange}
                  onSearch={(val) => setSearchStateText(val)}
                  onKeyDown={focusNext(address1Ref)}
                  filterOption={(input, option) =>
                    (option?.value ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase()) ||
                    (option?.statecode ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={stateOptions}
                  loading={loadingState}
                />
              </div>
              <div style={{ flex: 0.7 }}>
                <label style={labelStyle}>State Code</label>
                <Input
                  ref={stateCodeRef}
                  value={formValues?.stateCode}
                  disabled
                  placeholder="Code"
                  style={{
                    backgroundColor: "#f9fafb",
                    color: "#374151",
                    fontWeight: 600,
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Location Type</label>
                <Input
                  value={formValues?.locationType || "LOCAL"}
                  disabled
                  placeholder="Station"
                  style={{
                    backgroundColor: "#f9fafb",
                    color: "#374151",
                    fontWeight: 600,
                  }}
                />
              </div>
            </div>
          </SectionCard>

          <SectionCard id="address" icon={<HomeOutlined />} title="Address" index={2}>
            <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Address Line 1</label>
                <Input
                  ref={address1Ref}
                  value={formValues?.address1}
                  onChange={onInput("address1")}
                  onFocus={(e) => e.target.select()}
                  onKeyDown={focusNext(address2Ref)}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Address Line 2</label>
                <Input
                  ref={address2Ref}
                  value={formValues?.address2}
                  onChange={onInput("address2")}
                  onFocus={(e) => e.target.select()}
                  onKeyDown={focusNext(address3Ref)}
                />
              </div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Address Line 3</label>
                <Input
                  ref={address3Ref}
                  value={formValues?.address3}
                  onChange={onInput("address3")}
                  onFocus={(e) => e.target.select()}
                  onKeyDown={focusNext(address4Ref)}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Address Line 4</label>
                <Input
                  ref={address4Ref}
                  value={formValues?.address4}
                  onChange={onInput("address4")}
                  onFocus={(e) => e.target.select()}
                  onKeyDown={focusNext(gstinRef)}
                />
              </div>
            </div>
          </SectionCard>

          <SectionCard id="tax" icon={<IdcardOutlined />} title="Tax & Important dates" index={3}>
            <div style={{ display: "flex", gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>GSTIN</label>
                <Input
                  ref={gstinRef}
                  value={formValues?.gstin}
                  onChange={onInput("gstin")}
                  onFocus={(e) => e.target.select()}
                  onKeyDown={focusNextDeferred(dobRef)}
                  placeholder="GSTIN number"
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}><CalendarOutlined /> Date of Birth</label>
                <DatePicker
                  ref={dobRef}
                  style={{ width: "100%" }}
                  format={DATE_DISPLAY_FORMAT}
                  placeholder="DD-MM-YYYY"
                  value={formValues?.dob ? dayjs(formValues.dob) : null}
                  onChange={(d) => set("dob")(d ? d.format("YYYY-MM-DD") : "")}
                  onKeyDown={focusNextDeferred(anniversaryRef)}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}><CalendarOutlined /> Anniversary</label>
                <DatePicker
                  ref={anniversaryRef}
                  style={{ width: "100%" }}
                  format={DATE_DISPLAY_FORMAT}
                  placeholder="DD-MM-YYYY"
                  value={formValues?.anniversary ? dayjs(formValues.anniversary) : null}
                  onChange={(d) => set("anniversary")(d ? d.format("YYYY-MM-DD") : "")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      setTimeout(() => saveBtnRef.current?.focus?.(), 0);
                    }
                  }}
                />
              </div>
            </div>
          </SectionCard>

          {/* ===== Footer ===== */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6, paddingTop: 14, borderTop: "1px solid #E5E8EC" }}>
            {isEdit ? (
              <Popconfirm
                title="Delete this customer?"
                description="This cannot be undone."
                okText="Delete"
                okButtonProps={{ danger: true }}
                onConfirm={() => handleDelete?.(formValues?.mobile1)}
              >
                <motion.button
                  style={{
                    display: "flex", alignItems: "center", gap: 8, background: gradients.delete,
                    border: "none", color: "#fff", borderRadius: 10, padding: "9px 16px",
                    fontWeight: 700, fontSize: 13, cursor: "pointer",
                  }}
                  whileHover={{ y: -2, boxShadow: "0 10px 22px rgba(190,18,60,0.4)" }}
                  whileTap={{ scale: 0.96, y: 0 }}
                  animate={{ boxShadow: "0 4px 12px rgba(190,18,60,0.28)" }}
                  transition={{ duration: 0.2 }}
                >
                  <DeleteOutlined /> Delete
                </motion.button>
              </Popconfirm>
            ) : <span />}

            <div style={{ display: "flex", gap: 10 }}>
              <motion.button
                onClick={handleCancel}
                style={{
                  background: "#fff", border: "1.5px solid #CBD5E1", color: "#475569",
                  borderRadius: 10, padding: "9px 18px", fontWeight: 600, fontSize: 13, cursor: "pointer",
                }}
                whileHover={{ backgroundColor: "#F8FAFC", borderColor: "#94A3B8" }}
                whileTap={{ scale: 0.96 }}
              >
                Cancel
              </motion.button>

              <motion.button
                ref={saveBtnRef}
                onClick={handleSave}
                disabled={!isValid || saving}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  background: !isValid || saving ? "linear-gradient(135deg, #D1D5DB, #9CA3AF)" : saveGrad,
                  border: "none", color: "#fff", borderRadius: 10, padding: "9px 20px",
                  fontWeight: 700, fontSize: 13, cursor: !isValid || saving ? "not-allowed" : "pointer",
                }}
                whileHover={!isValid || saving ? {} : { y: -2, boxShadow: `0 10px 22px ${saveGlow}` }}
                whileTap={!isValid || saving ? {} : { scale: 0.96, y: 0 }}
                animate={{ boxShadow: !isValid || saving ? "none" : `0 4px 12px ${saveGlow}` }}
                transition={{ duration: 0.2 }}
              >
                {isEdit ? <SaveOutlined /> : <PlusCircleOutlined />}
                {saving ? "Saving..." : isEdit ? "Update Customer (Alt+S)" : "Save Customer (Alt+S)"}
              </motion.button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CustomerDialog;